import { auth } from "../../API/Auth";
import { EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import * as styles from "./KnowledgeBaseStyles";
import { useEffect, useState, useRef } from "react";
import { Button, Input, Space } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.js";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

function KnowledgeBase({ onManualOpen }: { onManualOpen?: (fileName: string) => void }) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      // Block Save / Print shortcuts while viewer open
      if (pdfUrl && ctrl && (e.key === "s" || e.key === "S" || e.key === "p" || e.key === "P")) {
        e.preventDefault();
        e.stopPropagation();
      }
      // block Ctrl+Shift+S
      if (pdfUrl && ctrl && e.shiftKey && (e.key === "S" || e.key === "s")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (pdfUrl) {
      window.addEventListener("keydown", handleKeyDown as any, true);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown as any, true);
    };
  }, [pdfUrl]);

  // const url = new URL(window.location.href);
  const pdfHeight =
    (window.innerHeight + window.innerHeight * 0.095).toString() + "px";
  const allManuals: {
    title: string;
    fileName: string;
    permission: string[];
  }[] = [
    {
      title: "Central IRB User Guide",
      fileName: "Central-IRB-User-Guide.pdf",
      permission: ["17"],
    },
    {
      title: "Principal Investigator & Other Site Personnel User Guide",
      fileName: "Principal-Investigator-&-Other-Site-Personnel-User-Guide.pdf",
      permission: ["15", "16"],
    },
    {
      title: "Safety Team Administrator User Guide",
      fileName: "Safety-Team-Administrator-User-Guide.pdf",
      permission: ["12"],
    }, 
    {
      title: "Safety Team User Guide",
      fileName: "Safety-Team-User-Guide.pdf",
      permission: ["13"],
    },
    {
      title: "Sponsor User Guide",
      fileName: "Sponsor-User-Guide.pdf",
      permission: ["14"],
    },
  ];

  // Hand pdf.js the same-origin URL, not a blob: URL - the server's
  // Content-Security-Policy (connect-src 'self') blocks pdf.js from reading blob: URLs.
  const downloadUserManual = (fileName: string) => {
    setError(null);
    setPdfUrl(`/Assets/${fileName}`);
    setPageNumber(1);
  };

  return (
    <styles.Container className="manual-container">
      {allManuals?.map((manual) =>
        auth.isPermissionExists(manual.permission) ? (
          <div
            className="manual"
            onClick={() => {
              if (onManualOpen) onManualOpen(manual.fileName);
              else downloadUserManual(manual.fileName);
            }}
            onKeyDown={() => {}}
          >
            {manual.title}
            <EyeOutlined />
          </div>
        ) : (
          <></>
        )
      )}
      {pdfUrl && (
        <>
          <div
            ref={viewerRef}
            className="pdf-viewer-container"
            style={{ position: "relative", height: pdfHeight }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div
              className="pdf-toolbar"
              style={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
            >
              <Space>
                <Button
                  size="small"
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                >
                  Prev
                </Button>
                <Input
                  size="small"
                  style={{ width: 72, textAlign: "center" }}
                  value={`${pageNumber} / ${numPages || "-"}`}
                  onChange={(e) => {
                    const val = parseInt(e.target.value.split(" ")[0]);
                    if (!isNaN(val) && val >= 1 && val <= numPages) setPageNumber(val);
                  }}
                />
                <Button
                  size="small"
                  onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))}
                  disabled={pageNumber >= numPages}
                >
                  Next
                </Button>
                <Button size="small" onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}>
                  -
                </Button>
                <Button size="small" onClick={() => setScale((s) => Math.min(3, s + 0.25))}>
                  +
                </Button>
              </Space>
            </div>

            <div
              className="pdf-canvas-area"
              style={{ width: "100%", height: "100%", overflow: "auto", position: "relative" }}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              {error && <div className="pdf-error">{error}</div>}
              {!error && (
                <Document
                  file={pdfUrl}
                  loading={<div className="pdf-loading">Loading...</div>}
                  onLoadSuccess={({ numPages: n }: any) => setNumPages(n)}
                  onLoadError={() => setError("Unable to render document.")}
                >
                  <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
                    <Page pageNumber={pageNumber} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
                  </div>
                </Document>
              )}
            </div>

            <div
              className="pdf-overlay"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                // overlay intercepts right-clicks but allows toolbar clicks because toolbar has higher z-index
                background: "transparent",
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          <Button
            className="pdf-close-btn"
            type="primary"
            onClick={() => {
              setPdfUrl(null);
              setNumPages(0);
              setPageNumber(1);
              setScale(1);
              setError(null);
            }}
          >
            <ArrowLeftOutlined />
            Back
          </Button>
        </>
      )}
    </styles.Container>
  );
}

export default KnowledgeBase;
